import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLanguageID,
  setLanguageWords,
  updateBlockedDates,
  setFreeTablesData,
  setExtraProductData,
  setCombinationList,
  setAvailableLocationData,
} from "../store/slice/Activity.slice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FetchWidgetConfigDetails,
  GetGuestDetailsByKey,
  getExtraProductDetails,
  getLanguageDetails,
  getWidgetSettings,
  getWidgetTitleandInfo,
} from "../api/HorecaWidget.service";
import {
  setLanguageLoaded,
  updateButtonText,
  updateCombinationLang,
  updateGuestInfo,
  updateLanguageData,
  updateWidgetConfig,
  updateWidgetSetting,
} from "../store/slice/widgetSetting.slice";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  fetchActivitesTime,
  fetchActivities,
  fetchAvailableLocation,
  fetchClosedActivityDates,
  getCombinationList,
} from "../api/activity.service";
import {
  setActivityData,
  setActivityDataLoading,
  updateDefaultLanguage,
  checkGuestLimit,
  updateTotalCount,
} from "../store/slice/Activity.slice";
import { useTheme } from "@emotion/react";
import { calculateEndTime, convertToTimeFormat } from "../utils/utils";
dayjs?.extend(customParseFormat); // Extend for custom date format parsing

let loadSettings = null;
let widgetIdSettings = null;

const useActivityWidget = () => {
  const {
    selectedDate,
    reservType,
    adult,
    defaultLanguage,
    freeTablesData,
    child,
    baby,
    toddler,
    totalCount,
    time,
    kid,
    activityDuration,
  } = useSelector((store) => store.Activity);
  const {
    key,
    languageData,
    widgetSetupId,
    widgetConfig,
    buttonText,
    guestId,
    resType,
  } = useSelector((store) => store?.widgetSettings);
  const { isOpenWidget } = useSelector((store) => store?.addGuest);
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const [langLoading, setLangLoading] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openWidget, setOpenWidget] = useState(
    location?.state?.openWidgetScreen ?? isOpenWidget
  );
  const prevLang = useRef(defaultLanguage);
  const prevDate = useRef(selectedDate);
  const idReservTypeRef = useRef(reservType?.idreservationtype);
  const getWidgetPositionStyle = (position, btn) => {
    switch (position) {
      case "Top-Left":
        return { top: "0", left: "0", transform: "translate(0, 0)" };
      case "Top-Middle":
        return { top: "0", left: "50%", transform: "translateX(-50%)" };
      case "Top-Right":
        return { top: "0", right: "0", transform: "translate(0, 0)" };
      case "Middle-Left":
        return { top: "50%", left: "0", transform: "translateY(-50%)" };
      case "Middle-Center":
        return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
      case "Middle-Right":
        return { top: "50%", right: "0", transform: "translateY(-50%)" };
      case "Bottom-Left":
        return { bottom: "0", left: "0", transform: "translate(0, 0)" };
      case "Bottom-Middle":
        return { bottom: "0", left: "50%", transform: "translateX(-50%)" };
      case "Bottom-Right":
        return { bottom: "0", right: "0", transform: "translate(0, 0)" };
      default:
        // Center by default if position not recognized
        if (btn) {
          return { bottom: "0", right: "0", transform: "translate(0, 0)" };
        } else {
          return {
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          };
        }
    }
  };
  const fetchLanguageDetails = async () => {
    if (languageData.length > 0) return;
    setLangLoading(true);
    try {
      const response = await getLanguageDetails(key);
      if (response?.status != 200) {
        console.error("Error fetching language details widget:", response);
        return;
      }
      dispatch(updateLanguageData(response?.data));
      const langParam = queryParams.get("lang");
      if (response?.data) {
        const defaultLang =
          !defaultLanguage && !langParam
            ? response?.data?.find((item) => item?.defaultLanguage)?.langcode
            : langParam
            ? langParam
            : defaultLanguage;

        localStorage.setItem(
          "widget",
          JSON.stringify({
            id: widgetSetupId,
            key: key,
            lang: defaultLang,
            resType: resType,
          })
        );

        dispatch(updateDefaultLanguage(defaultLang));
        dispatch(setLanguageLoaded(true)); // Set the flag to true
        dispatch(setLanguageID(defaultLang?.idlanguage));
      }
      setLangLoading(false);
    } catch (error) {
      console.error("Error fetching language details widget:", error?.message);
      setLangLoading(false);
    }
  };
  const fetchWidgetSettings = async (language) => {
    setWidgetLoading(true);
    try {
      const response = await getWidgetSettings(language, key);
      loadSettings = response;
      dispatch(updateWidgetSetting(response?.data));
      setWidgetLoading(false);
    } catch (error) {
      setWidgetLoading(false);
      console.error("Error fetching widget settings:", error?.message);
    }
  };
  const fetchWidgetSettingsDetails = async (lang) => {
    try {
      setWidgetLoading(true);
      const response = await FetchWidgetConfigDetails(
        key,
        widgetSetupId,
        lang,
        guestId
      );
      if (response?.status != 200 || !response?.data) {
        throw new Error(
          "Failed to fetch widget settings details. Please try again later."
        );
      }
      const result = response?.data?.basicLanguages?.reduce((acc, item) => {
        const tag = item.tag.replace(/{|}/g, "").replace(/\s+/g, "");
        const data = item.widgetTranslateLanguages[0]?.name;
        acc[tag] = data; // Assign data to the key acc[tag]
        return acc;
      }, {});
      dispatch(updateWidgetConfig(response?.data));
      dispatch(updateButtonText(response?.data?.fields?.reservationButtonText));
      dispatch(setLanguageWords(result));
      dispatch(
        updateCombinationLang({
          bookReservation: result?.bookReservation,
          selectCategory: result?.selectCategory,
        })
      );
      setWidgetLoading(false);
    } catch (error) {
      console.error("Error fetching widget settings details:", error);
      setWidgetLoading(false);
    }
  };
  const fetchGuestDetailsByToken = async () => {
    try {
      const response = await GetGuestDetailsByKey(
        widgetConfig?.token,
        key,
        guestId
      );
      if (!response) {
        throw new Error(
          "Failed to fetch guest details. Please try again later."
        );
      }
      dispatch(updateGuestInfo(response));
    } catch (error) {
      console.error("Error fetching guest details by token:", error);
    }
  };
  const fetchWidgetTitleandInfo = async (widgetSetupId, language) => {
    try {
      const response = await getWidgetTitleandInfo(widgetSetupId, language);
      widgetIdSettings = response;
    } catch (error) {
      console.error("Error fetching widget settings:", error?.message);
    }
  };
  const getUnavailableCalenderDetails = async () => {
    if (freeTablesData?.length > 0) return;
    const response = await fetchClosedActivityDates(key);
    if (response.success === false) {
      console.error(response.message); // Set the error message
    } else {
      dispatch(updateBlockedDates(response));
    }
    dispatch(setActivityDataLoading(false));
  };
  const getTimeDetails = async () => {
    //  if (prevDate.current == selectedDate) return;
    const parsedDate = dayjs(selectedDate, "DD-MM-YYYY");
    const formattedDate = parsedDate?.format("YYYY-MM-DD");
    const payload = {
      idwidgetsetup: widgetSetupId,
      key: key,
      date: formattedDate,
      idreservationType: reservType?.idreservationtype,
      noOfGuest: parseInt(totalCount),
      durationTimeValue:
        activityDuration?.timeValue || reservType?.products[0]?.timeValue,
    };
    const response = await fetchActivitesTime(payload);
    if (response.success === false) {
      console.error(response.message); // Set the error message
    } else {
      dispatch(setFreeTablesData(response));
    }
  };
  const fetchExtraTicketProducts = async () => {
    try {
      const response = await getExtraProductDetails(
        reservType?.idreservationtype,
        defaultLanguage
      );
      if (response?.status == 200) {
        dispatch(setExtraProductData(response));
      }
    } catch (error) {
      console.log("error extra product", error?.message);
    }
  };

  const fetchCombinations = async () => {
    setLoading(true);
    dispatch(setCombinationList([]));
    const endTime = calculateEndTime(
      time,
      convertToTimeFormat(activityDuration?.name)
    );
    const parsedDate = dayjs(selectedDate, "DD-MM-YYYY", true);
    // Check if date is valid
    if (!parsedDate?.isValid()) {
      console.error("Invalid date:", selectedDate);
      return;
    }
    // Format the valid date into 'YYYY-MM-DD'
    const formattedDate = parsedDate.format("YYYY-MM-DD");
    const payload = {
      key: key,
      idreservationtype: parseInt(reservType?.idreservationtype),
      ArrivalDate: formattedDate,
      ArrivalTime: time,
      DepatureTime: endTime,
      Field1: parseInt(adult),
      Field2: parseInt(child),
      Field3: parseInt(kid),
      Field4: parseInt(toddler),
      Field5: parseInt(baby),
      LanguageCode: defaultLanguage,
      NoOfGuest: parseInt(totalCount),
    };
    try {
      const response = await getCombinationList(payload);
      dispatch(setCombinationList(response));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const fetchAvailableLocationForActivites = async () => {
    const starttime = time; // e.g., "11:00:00"
    const secondsToAdd = reservType?.duration || 0; // fallback to 0 if undefined
    const times = dayjs(`2024-01-01T${starttime}`);
    let departureTime;
    const timeValue = activityDuration?.timeValue;
    if (timeValue && typeof timeValue === "string") {
      const parts = timeValue.split("/")?.map(Number);
      const [yy, mon, dd, hh, min] = parts;
      const totalSeconds =
        yy * 365 * 24 * 60 * 60 +
        mon * 30 * 24 * 60 * 60 +
        dd * 24 * 60 * 60 +
        hh * 60 * 60 +
        min * 60;

      departureTime = times.add(totalSeconds, "second").format("HH:mm:ss");
    } else {
      departureTime = times.add(secondsToAdd, "second").format("HH:mm:ss");
    }
    const payload = {
      idcompany: widgetConfig?.settings?.idcompany,
      ArrivalDate: dayjs(selectedDate, "yyyy-mm-dd"),
      ArrivalTime: starttime,
      DepartureTime: departureTime,
      idreservationtype: reservType?.idreservationtype,
      NoOfGuest: totalCount,
    };
    try {
      const response = await fetchAvailableLocation(payload);
      if (response?.status == 200) {
        dispatch(setAvailableLocationData(response?.data));
      } else {
        console.error("Failed to fetch available locations", response?.message);
      }
    } catch (error) {
      console.error("error", error);
    }
  };
  useEffect(() => {
    if (time) fetchAvailableLocationForActivites();
  }, [time]);

  useEffect(() => {
    if (widgetConfig?.token) {
      fetchGuestDetailsByToken();
    }
  }, [widgetConfig]);
  useEffect(() => {
    if (time) fetchCombinations();
  }, [time]);

  useEffect(() => {
    if (Object.keys(reservType)?.length > 0) {
      fetchExtraTicketProducts();
    }
  }, [reservType]);

  const getActivityDetails = useCallback(async () => {
    if (!selectedDate) return;
    const originalDate = dayjs(selectedDate, "DD-MM-YYYY").format("YYYY-MM-DD");
    dispatch(setActivityDataLoading(true));
    const response = await fetchActivities(
      key,
      defaultLanguage,
      widgetSetupId,
      originalDate,
      totalCount
    );
    if (response?.length > 0) {
      dispatch(setActivityData(response));
    } else {
      console.error(response.message); // Set the error message
    }
    dispatch(setActivityDataLoading(false));
  }, [defaultLanguage?.langcode, selectedDate]); // Dependencies

  useEffect(() => {
    if (
      defaultLanguage &&
      defaultLanguage?.trim() !== "" &&
      defaultLanguage !== prevLang.current
    ) {
      // Checks if defaultLanguage is non-empty and has changed from the previous value
      fetchWidgetSettingsDetails(defaultLanguage);
      prevLang.current = defaultLanguage; // Store the current language as previous value
    }
  }, [defaultLanguage]);

   useEffect(() => {
    fetchLanguageDetails();
  }, []);

  useEffect(() => {
    if (defaultLanguage && defaultLanguage !== prevLang.current) {
      fetchWidgetSettings(defaultLanguage);
      prevLang.current = defaultLanguage; // Store the current language as previous value
    }
  }, [defaultLanguage]);

  useEffect(() => {
    if (defaultLanguage && defaultLanguage !== prevLang.current) {
      fetchWidgetTitleandInfo(widgetSetupId, defaultLanguage);
      prevLang.current = defaultLanguage; // Store the current language as previous value
    }
  }, [defaultLanguage]);

  useEffect(() => {
    getUnavailableCalenderDetails();
  }, [key]);

  useEffect(() => {
    if (
      prevLang.current !== defaultLanguage ||
      prevDate.current !== selectedDate
    ) {
      getActivityDetails();
      prevLang.current = defaultLanguage; // Update ref to avoid unnecessary calls
      prevDate.current = selectedDate;
    }
  }, [selectedDate]);

  useEffect(() => {
    if (Object.keys(reservType)?.length > 0) {
      getTimeDetails();
    }
  }, [reservType]);

  const send = (
    frameHeight,
    frameWidth,
    top,
    right,
    bottom,
    left,
    floating,
    redirectUrl
  ) => {
    window.parent.postMessage(
      {
        reservationIframeHeight: frameHeight,
        reservationIframeWidth: frameWidth,
        reservationIframePositionTop: top,
        reservationIframePositionRight: right,
        reservationIframePositionBottom: bottom,
        reservationIframePositionLeft: left,
        reservationIframePositionFloating: floating,
        redirectUrl: redirectUrl,
      },
      "*"
    );
  };

  useEffect(() => {
    dispatch(updateTotalCount(adult));
  }, [adult]);
  useEffect(() => {
    if (adult > 10) {
      dispatch(
        checkGuestLimit({
          guestType: "adult",
          isGreaterThan10: true,
        })
      );
    } else {
      dispatch(
        checkGuestLimit({
          guestType: "adult",
          isGreaterThan10: false,
        })
      );
    }
  }, [adult]);
  return {
    selectedDate,
    reservType,
    adult,
    defaultLanguage,
    freeTablesData,
    child,
    baby,
    toddler,
    totalCount,
    time,
    kid,
    activityDuration,
    key,
    languageData,
    widgetSetupId,
    widgetConfig,
    buttonText,
    guestId,
    resType,
    isOpenWidget,
    location,
    idReservTypeRef,
    openWidget, 
    loading, 
    setLoading,
    langLoading, 
    widgetLoading,
     setWidgetLoading,
    setLangLoading,
    setOpenWidget,
    dispatch,
    theme,
    send,
    getWidgetPositionStyle
    
  };
};

export default useActivityWidget;
